import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Upload, FileText, Trash2, PanelLeft, Save, CheckCircle, AlertCircle, Loader2, Code2, BookOpen, BarChart3, Link2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import EmbedInstructions from "@/components/EmbedInstructions";
import AnalyticsView from "@/components/AnalyticsView";
import InvitationsView from "@/components/InvitationsView";
import PasswordResetAdmin from "@/components/PasswordResetAdmin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DocumentCard({ doc, onDelete }) {
  const sizeKB = (doc.size / 1024).toFixed(1);
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-5 hover:border-zinc-700 transition-all group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
            <FileText size={18} className="text-emerald-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-zinc-200 truncate">{doc.filename}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{sizeKB} KB</p>
          </div>
        </div>
        <button
          data-testid={`delete-doc-${doc.id}`}
          onClick={() => onDelete(doc.id)}
          className="opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-red-500/10 text-zinc-500 hover:text-red-400 transition-all"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <div className="mt-3 flex items-center gap-1.5">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Activo</span>
      </div>
    </motion.div>
  );
}

export default function AdminView({ onToggleSidebar, sidebarOpen }) {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [customInstructions, setCustomInstructions] = useState("");
  const [savedInstructions, setSavedInstructions] = useState(false);
  const [savingInstructions, setSavingInstructions] = useState(false);
  const [adminTab, setAdminTab] = useState("analytics"); // "analytics", "knowledge" or "embed"
  const fileInputRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/documents`);
      setDocuments(res.data);
    } catch (e) {
      console.error("Error fetching documents", e);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`);
      setCustomInstructions(res.data.custom_instructions || "");
    } catch (e) {
      console.error("Error fetching settings", e);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
    fetchSettings();
  }, [fetchDocuments, fetchSettings]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        await axios.post(`${API}/admin/documents`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        console.error("Error uploading", e);
      }
    }
    await fetchDocuments();
    setUploading(false);
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/admin/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      console.error("Error deleting document", e);
    }
  };

  const handleSaveInstructions = async () => {
    setSavingInstructions(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        custom_instructions: customInstructions,
      });
      setSavedInstructions(true);
      setTimeout(() => setSavedInstructions(false), 2000);
    } catch (e) {
      console.error("Error saving settings", e);
    }
    setSavingInstructions(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-900/80 flex-shrink-0 bg-zinc-950/80 backdrop-blur-sm">
        {!sidebarOpen && (
          <button
            data-testid="admin-open-sidebar-btn"
            onClick={onToggleSidebar}
            className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <PanelLeft size={18} />
          </button>
        )}
        <div>
          <h2
            className="text-xl font-bold tracking-tight text-zinc-50"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
          >
            Panel de Administración
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Gestiona conocimiento, instrucciones y el widget embebible
          </p>
        </div>
      </div>

      {/* Admin Tabs */}
      <div className="px-6 pt-4 flex gap-2 flex-shrink-0 flex-wrap">
        <button
          data-testid="admin-tab-analytics"
          onClick={() => setAdminTab("analytics")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all ${
            adminTab === "analytics"
              ? "bg-emerald-500 text-zinc-950"
              : "bg-zinc-900 text-zinc-400 hover:text-zinc-200 border border-zinc-800"
          }`}
        >
          <BarChart3 size={13} />
          Analíticas
        </button>
        <button
          data-testid="admin-tab-knowledge"
          onClick={() => setAdminTab("knowledge")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all ${
            adminTab === "knowledge"
              ? "bg-emerald-500 text-zinc-950"
              : "bg-zinc-900 text-zinc-400 hover:text-zinc-200 border border-zinc-800"
          }`}
        >
          <BookOpen size={13} />
          Base de Conocimiento
        </button>
        <button
          data-testid="admin-tab-embed"
          onClick={() => setAdminTab("embed")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all ${
            adminTab === "embed"
              ? "bg-emerald-500 text-zinc-950"
              : "bg-zinc-900 text-zinc-400 hover:text-zinc-200 border border-zinc-800"
          }`}
        >
          <Code2 size={13} />
          Widget Embebible
        </button>
        <button
          data-testid="admin-tab-invites"
          onClick={() => setAdminTab("invites")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all ${
            adminTab === "invites"
              ? "bg-emerald-500 text-zinc-950"
              : "bg-zinc-900 text-zinc-400 hover:text-zinc-200 border border-zinc-800"
          }`}
        >
          <Link2 size={13} />
          Invitaciones
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {adminTab === "embed" ? (
            <EmbedInstructions />
          ) : adminTab === "analytics" ? (
            <AnalyticsView />
          ) : adminTab === "invites" ? (
            <div className="space-y-6">
              <InvitationsView />
              <PasswordResetAdmin />
            </div>
          ) : (
            <>
          {/* Upload Zone */}
          <div
            data-testid="upload-zone"
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${
              dragOver
                ? "border-emerald-500 bg-emerald-500/5"
                : "border-zinc-800 hover:border-zinc-600 bg-zinc-900/20"
            }`}
          >
            <input
              ref={fileInputRef}
              data-testid="file-input"
              type="file"
              multiple
              accept=".txt,.pdf,.md,.csv,.doc,.docx"
              onChange={(e) => handleUpload(e.target.files)}
              className="hidden"
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 size={32} className="text-emerald-400 animate-spin" />
                <p className="text-sm text-zinc-400">Subiendo documentos...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-zinc-800/80 flex items-center justify-center">
                  <Upload size={24} className="text-zinc-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-300">
                    Arrastra archivos aquí o haz clic para seleccionar
                  </p>
                  <p className="text-xs text-zinc-600 mt-1">
                    TXT, PDF, MD, CSV, DOC — El contenido será usado como contexto por Edgar IA
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Documents Grid */}
          {documents.length > 0 && (
            <div>
              <h3
                className="text-sm font-semibold text-zinc-300 mb-4 tracking-wide uppercase"
                style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
              >
                Documentos ({documents.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <AnimatePresence>
                  {documents.map((doc) => (
                    <DocumentCard key={doc.id} doc={doc} onDelete={handleDelete} />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}

          {/* Custom Instructions */}
          <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-6">
            <h3
              className="text-sm font-semibold text-zinc-300 mb-1 tracking-wide uppercase"
              style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
            >
              Instrucciones Personalizadas
            </h3>
            <p className="text-xs text-zinc-500 mb-4">
              Define el tono, estilo o conocimiento adicional de Edgar IA
            </p>
            <textarea
              data-testid="custom-instructions-input"
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder="Ej: Cuando hablen de fichajes en Sudamérica, menciona siempre la importancia del mercado brasileño..."
              rows={5}
              className="w-full bg-zinc-950/60 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:border-zinc-600 transition-colors resize-none"
            />
            <div className="flex items-center justify-between mt-3">
              <AnimatePresence>
                {savedInstructions && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-1.5 text-emerald-400 text-xs"
                  >
                    <CheckCircle size={14} />
                    Guardado
                  </motion.div>
                )}
              </AnimatePresence>
              <button
                data-testid="save-instructions-btn"
                onClick={handleSaveInstructions}
                disabled={savingInstructions}
                className="flex items-center gap-2 bg-emerald-500 text-zinc-950 font-semibold rounded-full px-5 py-2 text-sm transition-all hover:bg-emerald-400 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 shadow-[0_0_20px_rgba(16,185,129,0.2)] ml-auto"
              >
                {savingInstructions ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Save size={14} />
                )}
                Guardar
              </button>
            </div>
          </div>

          {/* Info */}
          <div className="bg-zinc-900/20 border border-zinc-800/30 rounded-2xl p-5 flex items-start gap-3">
            <AlertCircle size={18} className="text-zinc-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                Los documentos que subas serán procesados como texto y usados como contexto de referencia 
                por Edgar IA al responder preguntas de tus alumnos. Para mejores resultados, sube contenido 
                en formato de texto plano (.txt, .md).
              </p>
            </div>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
